import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useAppDispatch, RootState, AppDispatch } from '../../redux/store'
import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined'
import LaunchIcon from '@mui/icons-material/Launch'
import { NavLink, useNavigate } from 'react-router-dom'
import DeleteButton from '../../components/common/DeleteButton'
import PaginationComp from '../../components/common/pagination'
import { useTranslation } from 'react-i18next'
import '../../i18n'
import { deleteRisque, getRisques } from '../../redux/risques/risqueActions'
import UseAlert from '../../components/common/alert'
import { hasRole } from '../../utils/checkRole'
import { UserRole } from '../../types/keycloak'
import { formatTitle } from '../../utils/formatTitle'
import { Risque } from '../../types/risqueTypes'
import {
  ListPageShell,
  listPageChipSx,
  listPageIconBtnStyle,
} from '../../components/common/ListPageShell'
import { priorityLabelsTranslations } from '../../utils/constants'
import i18n from '../../i18n'
import '../../components/common/ListPageTable.css'
import TableEmptyState from '../../components/common/TableEmptyState'
import TableLoadingState from '../../components/common/TableLoadingState'
import { ListPageDescription } from '../../components/common/ListPageDescription'
import { ListPageDateCell } from '../../components/common/ListPageCells'
import { onClickableTableRow } from '../../utils/tableRowClick'
import { useTableLoadingGate } from '../../hooks/useTableLoadingGate'
import { pageCountFromTotal, registerHeaderCount } from '../../utils/listCounts'

type RiskFilter = 'all' | 'critical' | 'unassigned' | 'overdue'

const CRITICALITY_PILL: Record<
  number,
  { bg: string; color: string; label: string }
> = {
  1: { bg: '#FAE6E3', color: '#96271B', label: 'Critical' },
  2: { bg: '#FAEFDA', color: '#84540A', label: 'High' },
  3: { bg: '#E6EFF8', color: '#22548A', label: 'Medium' },
  4: { bg: '#E1F4F0', color: '#076B5C', label: 'Low' },
}

const RISK_PRIORITY_PILL: Record<
  number,
  { bg: string; color: string }
> = {
  1: { bg: '#FAE6E3', color: '#96271B' },
  2: { bg: '#E6EFF8', color: '#22548A' },
  3: { bg: '#EAF0F0', color: '#5F7484' },
}

function Pill({
  label,
  bg,
  color,
}: {
  label: string
  bg: string
  color: string
}) {
  return (
    <span className="incyia-pill" style={{ background: bg, color }}>
      <span className="incyia-pill-dot" />
      {label}
    </span>
  )
}

function DateCell({
  value,
  locale,
}: {
  value?: string | Date
  locale: string
}) {
  return <ListPageDateCell value={value} locale={locale} />
}

function isUnassigned(risk: Risque): boolean {
  return !risk.PlanId && !risk.PlanName
}

function isOverdue(risk: Risque): boolean {
  if (!risk.Deadline) return false
  const end = new Date(risk.Deadline)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  end.setHours(0, 0, 0, 0)
  return end < today
}

export default function RisqueListe() {
  const { t } = useTranslation()
  const locale = i18n.language
  const dispatch: AppDispatch = useAppDispatch()
  const navigate = useNavigate()
  const roles = useSelector((state: RootState) => state.auth.roles)

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [search, setSearch] = useState('')
  const [riskFilter, setRiskFilter] = useState<RiskFilter>('all')
  const [alert, setAlert] = useState({
    open: false,
    message: '',
    severity: '' as 'info' | 'success' | 'error' | 'warning',
  })

  const risquesList = useSelector(
    (state: RootState) => state.risque.RisqueResponse?.risques
  )
  const totalRisks = useSelector(
    (state: RootState) => state.risque.RisqueResponse?.total ?? 0
  )
  const totalPages = useSelector(
    (state: RootState) => state.risque.RisqueResponse?.totalPages
  )
  const loading = useSelector((state: RootState) => state.risque.loading)
  const showLoader = useTableLoadingGate(loading, [page, pageSize, search])

  useEffect(() => {
    dispatch(getRisques({ search, page, pageSize }))
  }, [dispatch, page, pageSize, search])

  const filteredRisks = useMemo(() => {
    const rows = risquesList ?? []
    if (riskFilter === 'all') return rows
    if (riskFilter === 'critical') {
      return rows.filter((r) => Number(r.Criticality) === 1)
    }
    if (riskFilter === 'unassigned') {
      return rows.filter(isUnassigned)
    }
    return rows.filter(isOverdue)
  }, [risquesList, riskFilter])

  const canManage =
    hasRole(roles, UserRole.RSSI) ||
    hasRole(roles, UserRole.SSI_TEAM) ||
    hasRole(roles, UserRole.ADMIN)

  const handleDeleteClick = async (risqueId: string) => {
    try {
      dispatch(deleteRisque(risqueId))
      setAlert({
        open: true,
        message: t('alerts.onDelete.success'),
        severity: 'success',
      })
    } catch {
      setAlert({
        open: true,
        message: t('alerts.onDelete.error'),
        severity: 'error',
      })
    }
  }

  const pageCount = pageCountFromTotal(totalRisks, pageSize, totalPages)
  const headerCount = registerHeaderCount(
    totalRisks,
    filteredRisks.length,
    riskFilter === 'all'
  )

  const filterChips: { key: RiskFilter; label: string }[] = [
    { key: 'all', label: t('listPages.all') },
    { key: 'critical', label: t('listPages.critical') },
    { key: 'unassigned', label: t('listPages.unassigned') },
    { key: 'overdue', label: t('listPages.overdue') },
  ]

  return (
    <>
      <UseAlert
        open={alert.open}
        message={alert.message}
        severity={alert.severity}
        onClose={() => setAlert({ ...alert, open: false })}
      />

      <ListPageShell
        eyebrow={t('entityForms.risque.createEyebrow', {
          defaultValue: 'Management · Risks',
        })}
        title={t('risks')}
        titleTourId="risks-page-header"
        subtitle={t('listPages.risksSub')}
        addLabel={canManage ? t('buttons.addRisk') : undefined}
        onAdd={canManage ? () => navigate('/addrisque') : undefined}
        search={search}
        onSearchChange={(value) => {
          setSearch(value)
          setPage(1)
        }}
        searchPlaceholder={t('filterByName')}
        countLabel={t('listPages.risksCount', {
          count: headerCount,
        })}
        chips={
          <>
            {filterChips.map((chip) => (
              <button
                key={chip.key}
                type="button"
                style={listPageChipSx(riskFilter === chip.key)}
                onClick={() => setRiskFilter(chip.key)}
              >
                {chip.label}
              </button>
            ))}
          </>
        }
        footer={
          pageCount > 0 && riskFilter === 'all' ? (
            <PaginationComp
              page={page}
              pageSize={pageSize}
              onChange={(nextPage, newSize) => {
                setPage(nextPage)
                setPageSize(newSize)
              }}
              totalItems={pageCount}
            />
          ) : undefined
        }
      >
        <div className="incyia-table-wrap">
          {showLoader ? (
            <TableLoadingState />
          ) : filteredRisks.length === 0 ? (
            <TableEmptyState />
          ) : (
            <table className="incyia-table">
              <thead>
                <tr>
                  <th>{t('Name')}</th>
                  <th>{t('Description')}</th>
                  <th>{t('plan_name')}</th>
                  <th>{t('Mission Name')}</th>
                  <th>{t('Criticality')}</th>
                  <th>{t('Priority')}</th>
                  <th>{t('Deadline')}</th>
                  <th style={{ textAlign: 'right' }}>{t('Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredRisks.map((risk: Risque) => {
                  const criticality = Number(risk.Criticality)
                  const critStyle =
                    CRITICALITY_PILL[criticality] ?? CRITICALITY_PILL[4]
                  const priority = Number(risk.Priority)
                  const prioStyle =
                    RISK_PRIORITY_PILL[priority] ?? RISK_PRIORITY_PILL[3]

                  return (
                    <tr key={risk._id} onClick={onClickableTableRow}>
                      <td className="incyia-col-pinned">
                        <NavLink
                          to={`/risque-detail/${risk._id}`}
                          className="incyia-name-link"
                        >
                          <ListPageDescription
                            value={
                              risk.RisqueName
                                ? formatTitle(risk.RisqueName)
                                : undefined
                            }
                            tone="primary"
                          />
                        </NavLink>
                      </td>
                      <td className="incyia-wrap">
                        <ListPageDescription value={risk.Description} />
                      </td>
                      <td className="incyia-muted">
                        {isUnassigned(risk) ? (
                          <Pill
                            label={t('listPages.notLinked')}
                            bg="#EAF0F0"
                            color="#5F7484"
                          />
                        ) : (
                          <ListPageDescription value={risk.PlanName} />
                        )}
                      </td>
                      <td className="incyia-muted">
                        <ListPageDescription value={risk.MissionName} />
                      </td>
                      <td>
                        <Pill
                          label={
                            t(`priority.${criticality}`, {
                              defaultValue:
                                priorityLabelsTranslations[criticality] ||
                                critStyle.label,
                            }) || critStyle.label
                          }
                          bg={critStyle.bg}
                          color={critStyle.color}
                        />
                      </td>
                      <td>
                        <Pill
                          label={t(`prioritRisk.${priority}`, {
                            defaultValue: String(priority || '—'),
                          })}
                          bg={prioStyle.bg}
                          color={prioStyle.color}
                        />
                      </td>
                      <td>
                        <DateCell value={risk.Deadline} locale={locale} />
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <div className="incyia-row-actions">
                          <button
                            type="button"
                            title={t('listPages.openAction')}
                            onClick={() =>
                              navigate(`/risque-detail/${risk._id}`)
                            }
                            style={listPageIconBtnStyle}
                          >
                            <LaunchIcon sx={{ fontSize: 15 }} />
                          </button>
                          {canManage && (
                            <button
                              type="button"
                              title={t('listPages.edit')}
                              onClick={() =>
                                navigate(`/risque/${risk._id}`)
                              }
                              style={listPageIconBtnStyle}
                            >
                              <DriveFileRenameOutlineOutlinedIcon
                                sx={{ fontSize: 15 }}
                              />
                            </button>
                          )}
                          {canManage && (
                            <DeleteButton
                              onDelete={() =>
                                handleDeleteClick(risk._id as string)
                              }
                              item={{
                                entityType: t('risks', {
                                  defaultValue: 'Risk',
                                }),
                                name: risk.RisqueName
                                  ? formatTitle(risk.RisqueName)
                                  : undefined,
                                details: [
                                  {
                                    label: t('deleteConfirm.fields.status'),
                                    value: String(risk.status ?? '—'),
                                  },
                                  {
                                    label: t('deleteConfirm.fields.mission'),
                                    value: risk.MissionName || '—',
                                  },
                                ],
                              }}
                            />
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      </ListPageShell>
    </>
  )
}
